import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, NavLink } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  Sparkles,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  BookOpen,
  User,
  X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import countryData from "../../Data/countryCode.json";
import {
  INTERESTS,
  QUALIFICATIONS,
  RESULTS_SPM,
  RESULTS_UEC,
  RESULTS_CGPA,
  LOCATIONS,
  PATHWAYS,
  BUDGETS,
  TIMELINES,
  PARENT_OPTIONS,
  FILLER_OPTIONS,
} from "../../Data/studentData";
import type { Answers } from "../../types/CourseFinder";
import type { Student } from "../../types/student";
import {
  StepWrapper,
  StepHeading,
} from "../../Components/CourseFinder/Stepper";
import CourseFinderNav from "../../Components/CourseFinder/CourseFinderNav";
import ProgressBar from "../../Components/ProgressBar";
import MultiChip from "../../Components/CourseFinder/MultiChip";
import SingleOption from "../../Components/CourseFinder/SingleOption";
import LoadingScreen from "../../Components/CourseFinder/LoadingScreen";
import ResultsScreen, {
  getRecommendedCourses,
  getRecommendedUniversities,
  getReasons,
} from "../../Components/CourseFinder/ResultsScreen";

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getResultOptions(qual: string): string[] {
  if (qual === "UEC") return RESULTS_UEC;
  if (["Diploma", "Foundation", "STPM"].includes(qual)) return RESULTS_CGPA;
  if (qual === "Form 4") return ["Not applicable — I am still in Form 4"];
  return RESULTS_SPM;
}

type OptionWithLabel = {
  id: string;
  label: string;
  aliases?: string[];
};

function normalizeOptionValue(value?: string): string {
  return (value || "").trim().toLowerCase();
}

function matchOptionId(
  value: string | undefined,
  options: OptionWithLabel[],
): string {
  const normalized = normalizeOptionValue(value);
  if (!normalized) return "";

  return (
    options.find(
      (option) =>
        normalizeOptionValue(option.id) === normalized ||
        normalizeOptionValue(option.label) === normalized ||
        option.aliases?.some(
          (alias) => normalizeOptionValue(alias) === normalized,
        ),
    )?.id || ""
  );
}

function normalizeInterests(value?: string[]): string[] {
  if (!value?.length) return [];

  return value
    .map((interest) => matchOptionId(interest, INTERESTS))
    .filter(Boolean);
}

function getAnswerInterests(answers: Answers): string[] {
  return answers.interests.map((id) =>
    id === "Other" && answers.otherInterest
      ? answers.otherInterest
      : INTERESTS.find((i) => i.id === id)?.label || id,
  );
}

function getAnswerQualification(answers: Answers): string {
  return answers.qualifications === "Other" && answers.otherQualifications
    ? answers.otherQualifications
    : answers.qualifications;
}

function getAnswerEstimatedResult(answers: Answers): string {
  return answers.resultRange === "Other" && answers.otherResult
    ? answers.otherResult
    : answers.resultRange;
}

function getAnswerBudget(answers: Answers): string {
  return BUDGETS.find((b) => b.id === answers.budget)?.label || answers.budget;
}

function getCourseFinderRecommendations(answers: Answers) {
  const courses = getRecommendedCourses(answers.interests).map((course) => ({
    name: course.name,
    desc: course.desc,
  }));

  const universities = getRecommendedUniversities(
    answers.pathway,
    answers.budget,
    answers.interests,
  ).map((uni) => ({
    name: uni.uniName,
    location: uni.location,
    type: uni.type,
    feeRange: uni.feeRange,
    reasons: getReasons(uni, answers),
  }));

  return { courses, universities };
}

function normalizeProfileSchoolInfo(user?: Partial<Student> | null) {
  if (user?.school && typeof user.school === "object") {
    return {
      schoolName: user.school.schoolName || "",
      schoolYear: user.school.schoolYear || "",
    };
  }

  return {
    schoolName: typeof user?.school === "string" ? user.school : "",
    schoolYear: user?.spmYear ? String(user.spmYear) : "",
  };
}

function getProfileResultDetails(user?: Partial<Student> | null) {
  return user?.resultDetails || user?.spmGrades || [];
}

function computeProfileCompletion(u: Partial<Student>): number {
  const school = normalizeProfileSchoolInfo(u);
  const resultDetails = getProfileResultDetails(u);
  const personal: [boolean, number][] = [
    [!!u.name?.trim(), 5],
    [!!u.email?.trim(), 5],
    [!!u.phoneNumber?.trim(), 5],
    [!!u.ic?.trim(), 5],
    [!!u.dob?.trim(), 5],
    [!!u.gender?.trim(), 5],
    [!!u.state?.trim(), 5],
    [!!u.countryCode?.trim(), 5],
  ];
  const academic: [boolean, number][] = [
    [!!school.schoolName.trim(), 10],
    [!!school.schoolYear.trim(), 10],
    [resultDetails.length > 0, 10],
  ];
  const preferences: [boolean, number][] = [
    [!!u.pathway?.trim(), 10],
    [(u.interests?.length ?? 0) > 0, 10],
    [!!u.budget?.trim(), 10],
  ];

  return [...personal, ...academic, ...preferences].reduce(
    (sum, [ok, points]) => sum + (ok ? points : 0),
    0,
  );
}

function computeProfileCompletionFromCourseFinder(
  answers: Answers,
  user?: Partial<Student> | null,
): number {
  const interests = getAnswerInterests(answers);
  const budget = getAnswerBudget(answers);
  const profileData = {
    ...user,
    name: answers.name || user?.name || "",
    email: answers.email || user?.email || "",
    phoneNumber: answers.phoneNumber || user?.phoneNumber || "",
    countryCode: answers.countryCode || user?.countryCode || "",
    pathway: answers.pathway || user?.pathway || "",
    interests: interests.length ? interests : user?.interests || [],
    budget: budget || user?.budget || "",
  } as Partial<Student>;

  return computeProfileCompletion(profileData);
}

const DEFAULT_ANSWERS: Answers = {
  interests: [],
  otherInterest: "",
  otherQualifications: "",
  qualifications: "",
  otherResult: "",
  resultRange: "",
  locations: [],
  pathway: "",
  budget: "",
  timeline: "",
  parentInvolvement: "",
  name: "",
  phoneNumber: "",
  email: "",
  password: "",
  fillerType: "",
  countryCode: "60",
  country: "Malaysia",
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function CourseFinderPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    user,
    register,
    isAuthenticated,
    submitCourseFinderAnswers,
  } =
    useAuth();

  const urlInterest = searchParams.get("interest") || "";

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({ ...DEFAULT_ANSWERS });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isRegistering, setIsRegistering] = useState(false);

  const TOTAL_STEPS = 9;

  // Map URL interest to our interest ids
  useEffect(() => {
    if (!urlInterest) return;
    const lower = urlInterest.toLowerCase();
    const matched = INTERESTS.find(
      (i) =>
        lower.includes(i.label.toLowerCase().split(" /")[0].toLowerCase()) ||
        i.label.toLowerCase().includes(lower),
    );
    if (matched) setAnswers((prev) => ({ ...prev, interests: [matched.id] }));
  }, [urlInterest]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const savedInterests = normalizeInterests(
      user.interests?.length
        ? user.interests
        : user.courseFinderAnswers?.interests,
    );
    const savedQualification = matchOptionId(
      user.qualification ||
        user.qualifications ||
        user.courseFinderAnswers?.qualification ||
        user.courseFinderAnswers?.qualifications,
      QUALIFICATIONS,
    );
    const savedPathway = matchOptionId(
      user.pathway || user.courseFinderAnswers?.pathway,
      PATHWAYS,
    );
    const savedBudget = matchOptionId(
      user.budget || user.courseFinderAnswers?.budget,
      BUDGETS,
    );

    setAnswers((prev) => ({
      ...prev,
      interests: savedInterests.length ? savedInterests : prev.interests,
      qualifications: savedQualification || prev.qualifications,
      pathway: savedPathway || prev.pathway,
      budget: savedBudget || prev.budget,
    }));
  }, [isAuthenticated, user]);

  const updateAnswers = (updates: Partial<Answers>) =>
    setAnswers((prev) => ({ ...prev, ...updates }));
  const toggleInterest = (id: string) => {
    setAnswers((prev) => {
      const has = prev.interests.includes(id);
      if (id === "Not sure")
        return { ...prev, interests: has ? [] : [id] };
      const filtered = prev.interests.filter((i) => i !== "Not sure");
      return {
        ...prev,
        interests: has ? filtered.filter((i) => i !== id) : [...filtered, id],
      };
    });
  };
  const toggleLocation = (id: string) => {
    setAnswers((prev) => {
      const has = prev.locations.includes(id);
      if (id === "Not Sure") return { ...prev, locations: has ? [] : [id] };
      const filtered = prev.locations.filter((i) => i !== "Not Sure");
      return {
        ...prev,
        locations: has ? filtered.filter((i) => i !== id) : [...filtered, id],
      };
    });
  };

  const canProceed = (): boolean => {
    if (step === 1) return answers.interests.length > 0;
    if (step === 2) return !!answers.qualifications;
    if (step === 3) return !!answers.resultRange;
    if (step === 4) return answers.locations.length > 0;
    if (step === 5) return !!answers.pathway;
    if (step === 6) return !!answers.budget;
    if (step === 7) return !!answers.timeline;
    if (step === 8) return !!answers.parentInvolvement;
    return true;
  };

  const validateContact = (): boolean => {
    const e: Record<string, string> = {};
    const { name, phoneNumber, email, password } = answers;
    if (!name.trim()) e.name = "Full name is required";
    if (!phoneNumber.trim()) e.phoneNumber = "Phone number is required";
    if (!email.trim()) e.email = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = "Enter a valid email address";
    if (!password) e.password = "Password is required";
    else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(password))
      e.password =
        "Password must be at least 8 characters, including one uppercase letter, one lowercase letter, one number, and one symbol";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = async () => {
    if (step === 0) {
      setStep(1);
      return;
    }

    // Skip result step for Form 4 — they only have one option
    if (step === 2 && answers.qualifications === "Form 4") {
      setAnswers((prev) => ({
        ...prev,
        resultRange: "Not applicable — I am still in Form 4",
      }));
      setStep(4);
      return;
    }

    if (step === 8 && isAuthenticated) {
      setStep(10); // loading

      const payload = {
        courseFinderInterests: getAnswerInterests(answers),
        qualification: getAnswerQualification(answers),
        estimatedResult: getAnswerEstimatedResult(answers),
        studentResult: answers.studentResult || "",
        preferredStudyLocations: answers.locations,
        pathway: answers.pathway,
        budget: getAnswerBudget(answers),
        timeline: answers.timeline,
        parentInvolvement: answers.parentInvolvement,
        profileCompletion: computeProfileCompletionFromCourseFinder(
          answers,
          user,
        ),
        recommendations: getCourseFinderRecommendations(answers),
      };

      await submitCourseFinderAnswers(payload);

      setTimeout(() => setStep(11), 800);
      return;
    }

    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    if (step === 4 && answers.qualifications === "Form 4") {
      setStep(2);
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmitContact = async () => {
    if (!validateContact()) return;
    setIsRegistering(true);
    setStep(10); // loading

    try {
      const success = await register({
        name: answers.name,
        email: answers.email,
        phoneNumber: answers.phoneNumber,
        countryCode: answers.countryCode,
        country: answers.country,
        password: answers.password,
        fillerType: answers.fillerType,
        pathway: answers.pathway as "local" | "overseas" | "both",
        courseFinderInterests: getAnswerInterests(answers),
        qualification: getAnswerQualification(answers),
        estimatedResult: getAnswerEstimatedResult(answers),
        studentResult: answers.studentResult || "",
        preferredStudyLocations: answers.locations,
        budget: getAnswerBudget(answers),
        timeline: answers.timeline,
        parentInvolvement: answers.parentInvolvement,
        profileCompletion: computeProfileCompletionFromCourseFinder(answers),
        leadSource: "Website",
        sourceDetail: "Course Finder",
        courseFinderResult: {
          answers: {
            courseFinderInterests: getAnswerInterests(answers),
            qualification: getAnswerQualification(answers),
            estimatedResult: getAnswerEstimatedResult(answers),
            studentResult: answers.studentResult || "",
            preferredStudyLocations: answers.locations,
            pathway: answers.pathway,
            budget: getAnswerBudget(answers),
            timeline: answers.timeline,
            parentInvolvement: answers.parentInvolvement,
          },
          recommendations: getCourseFinderRecommendations(answers),
          completedAt: new Date().toISOString(),
        },
      } as any);
      setIsRegistering(false);
      if (success) {
        setTimeout(() => setStep(11), 800);
        return;
      }
      setStep(9); // go back to contact form
      setErrors({
        submit: "Registration failed. Please try again.",
        email: "Registration failed. This email may already be in use.",
      });
    } catch (error: any) {
      setIsRegistering(false);
      setStep(9);
      setErrors({
        submit: error?.message || "Registration failed. Please try again.",
        email:
          error?.code === "verification-required"
            ? "Verify this email, then sign in to continue."
            : "Registration failed. This email may already be in use.",
      });
    }
  };

  const handleGoToDashboard = () => navigate("/dashboard");

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-[#FAFAFA]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <CourseFinderNav answers={answers} />

      <main className="pt-20 pb-8">
        <div className="max-w-xl mx-auto px-4">
          {/* Step 10: Loading */}
          {step === 10 && (
            <div className="mt-16">
              <LoadingScreen />
            </div>
          )}

          {/* Step 11: Results */}
          {step === 11 && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ResultsScreen
                  answers={answers}
                  onGoToDashboard={handleGoToDashboard}
                />
              </motion.div>
            </AnimatePresence>
          )}

          {/* Steps 0-9 */}
          {step <= 9 && (
            <div className="mt-4">
              {step >= 1 && step <= 9 && (
                <ProgressBar current={step} total={TOTAL_STEPS} />
              )}

              <AnimatePresence mode="wait">
                {/* ─ Step 0: Welcome ─ */}
                {step === 0 && (
                  <StepWrapper key="step-0">
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center shadow-2xl shadow-violet-500/30 mx-auto mb-6">
                        <Sparkles className="w-7 h-7 text-white" />
                      </div>
                      <h1
                        className="text-[32px] text-slate-900 tracking-tight mb-3"
                        style={{ fontWeight: 900 }}
                      >
                        Find Courses That Suit You
                      </h1>
                      <p
                        className="text-[15px] text-slate-500 mb-6 leading-relaxed max-w-md mx-auto"
                        style={{ fontWeight: 400 }}
                      >
                        Tell us what you want to study, your results and your
                        budget. SCS Education will help you compare suitable
                        courses and universities.
                      </p>

                      {urlInterest && (
                        <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-violet-50 border border-violet-200 mb-6">
                          <BookOpen className="w-4 h-4 text-violet-600" />
                          <span
                            className="text-[13px] text-violet-700"
                            style={{ fontWeight: 600 }}
                          >
                            You're interested in: <strong>{urlInterest}</strong>
                          </span>
                          <button
                            onClick={() => updateAnswers({ interests: [] })}
                            className="text-violet-400 hover:text-violet-600 ml-1 cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                          onClick={handleNext}
                          className="flex items-center justify-center gap-2 px-8 py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] hover:shadow-lg hover:shadow-violet-500/30 hover:-translate-y-px transition-all duration-200 cursor-pointer"
                          style={{ fontWeight: 700 }}
                        >
                          Start Course Finder <ArrowRight className="w-4 h-4" />
                        </button>
                        <a
                          href="https://wa.me/601112870256"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border-2 border-slate-200 bg-white text-slate-700 text-[14px] hover:border-green-300 hover:text-green-700 transition-all"
                          style={{ fontWeight: 600 }}
                        >
                          <MessageCircle className="w-4 h-4" /> Talk to a
                          Counsellor
                        </a>
                      </div>

                      {/* Feature highlights */}
                      <div className="grid grid-cols-3 gap-3 mt-10">
                        {[
                          {
                            emoji: "🆓",
                            label: "100% Free",
                            sub: "No fees for students",
                          },
                          {
                            emoji: "⏱️",
                            label: "5 minutes",
                            sub: "Quick quiz",
                          },
                          {
                            emoji: "🎓",
                            label: "300+ unis",
                            sub: "Local & overseas",
                          },
                        ].map((f, i) => (
                          <div
                            key={i}
                            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center"
                          >
                            <div className="text-[24px] mb-1">{f.emoji}</div>
                            <p
                              className="text-[12px] text-slate-800"
                              style={{ fontWeight: 700 }}
                            >
                              {f.label}
                            </p>
                            <p
                              className="text-[11px] text-slate-400"
                              style={{ fontWeight: 400 }}
                            >
                              {f.sub}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </StepWrapper>
                )}

                {/* ─ Step 1: Study Interest ─ */}
                {step === 1 && (
                  <StepWrapper key="step-1">
                    <StepHeading
                      step="Step 1"
                      title="What are you interested in studying?"
                      sub="Select all that apply. You can choose more than one."
                    />
                    <div className="flex flex-wrap gap-2.5 mb-4">
                      {INTERESTS.map((item) => (
                        <MultiChip
                          key={item.id}
                          label={item.label}
                          emoji={item.emoji}
                          selected={answers.interests.includes(item.id)}
                          onClick={() => toggleInterest(item.id)}
                        />
                      ))}
                    </div>
                    {answers.interests.includes("Other") && (
                      <div className="mb-4">
                        <input
                          type="text"
                          placeholder="Tell us what you are interested in..."
                          value={answers.otherInterest}
                          onChange={(e) =>
                            updateAnswers({ otherInterest: e.target.value })
                          }
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                          style={{ fontWeight: 400 }}
                        />
                      </div>
                    )}
                  </StepWrapper>
                )}

                {/* ─ Step 2: Qualification ─ */}
                {step === 2 && (
                  <StepWrapper key="step-2">
                    <StepHeading
                      step="Step 2"
                      title="What is your current qualification?"
                      sub="Select your current study level."
                    />
                    <div className="flex flex-col gap-2.5">
                      {QUALIFICATIONS.map((q) => (
                        <SingleOption
                          key={q.id}
                          label={q.label}
                          sub={q.note}
                          selected={answers.qualifications === q.id}
                          highlight={q.id === "Form 4"}
                          onClick={() =>
                            updateAnswers({
                              qualifications: q.id,
                              resultRange: "",
                            })
                          }
                        />
                      ))}
                      {answers.qualifications === "Other" && (
                        <input
                          type="text"
                          placeholder="Tell us your result..."
                          value={answers.otherQualifications}
                          onChange={(e) =>
                            updateAnswers({
                              otherQualifications: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                          style={{ fontWeight: 400 }}
                        />
                      )}
                    </div>
                  </StepWrapper>
                )}

                {/* ─ Step 3: Result Range ─ */}
                {step === 3 && answers.qualifications !== "Form 4" && (
                  <StepWrapper key="step-3">
                    <StepHeading
                      step="Step 3"
                      title="What is your latest or expected result?"
                      sub="This helps SCS Education find more suitable course options for you."
                    />
                    <div className="flex flex-col gap-2.5">
                      {getResultOptions(answers.qualifications).map((r) => (
                        <SingleOption
                          key={r}
                          label={r}
                          selected={answers.resultRange === r}
                          onClick={() => updateAnswers({ resultRange: r })}
                        />
                      ))}
                      {answers.resultRange === "Other" && (
                        <input
                          type="text"
                          placeholder="Tell us your result..."
                          value={answers.otherResult}
                          onChange={(e) =>
                            updateAnswers({ otherResult: e.target.value })
                          }
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                          style={{ fontWeight: 400 }}
                        />
                      )}
                    </div>
                  </StepWrapper>
                )}

                {/* ─ Step 4: Study Location ─ */}
                {step === 4 && (
                  <StepWrapper key="step-4">
                    <StepHeading
                      step="Step 4"
                      title="Where do you prefer to study?"
                      sub="Select all that apply."
                    />
                    <div className="flex flex-wrap gap-2.5">
                      {LOCATIONS.map((loc) => (
                        <MultiChip
                          key={loc.id}
                          label={loc.label}
                          emoji={loc.emoji}
                          selected={answers.locations.includes(loc.id)}
                          onClick={() => toggleLocation(loc.id)}
                        />
                      ))}
                    </div>
                  </StepWrapper>
                )}

                {/* ─ Step 5: Local or Overseas ─ */}
                {step === 5 && (
                  <StepWrapper key="step-5">
                    <StepHeading
                      step="Step 5"
                      title="Are you looking for local or overseas study?"
                      sub="This helps us show you the right universities."
                    />
                    <div className="flex flex-col gap-2.5">
                      {PATHWAYS.map((p) => (
                        <SingleOption
                          key={p.id}
                          label={p.label}
                          sub={p.desc}
                          emoji={p.emoji}
                          selected={answers.pathway === p.id}
                          onClick={() => updateAnswers({ pathway: p.id })}
                        />
                      ))}
                    </div>
                    {(answers.pathway === "overseas" ||
                      answers.pathway === "both") && (
                      <div className="mt-4 p-3.5 rounded-2xl bg-amber-50 border border-amber-100">
                        <p
                          className="text-[12px] text-amber-700 leading-relaxed"
                          style={{ fontWeight: 500 }}
                        >
                          ⚠️ Overseas study may require additional checks for
                          visa, English requirements, fees and intake deadlines.
                          A counsellor will guide you through everything.
                        </p>
                      </div>
                    )}
                  </StepWrapper>
                )}

                {/* ─ Step 6: Budget ─ */}
                {step === 6 && (
                  <StepWrapper key="step-6">
                    <StepHeading
                      step="Step 6"
                      title="What is your estimated study budget?"
                      sub="This helps SCS Education suggest more realistic options for your family."
                    />
                    <div className="flex flex-col gap-2.5">
                      {BUDGETS.map((b) => (
                        <SingleOption
                          key={b.id}
                          label={b.label}
                          sub={b.sub}
                          selected={answers.budget === b.id}
                          onClick={() => updateAnswers({ budget: b.id })}
                        />
                      ))}
                    </div>
                  </StepWrapper>
                )}

                {/* ─ Step 7: Timeline ─ */}
                {step === 7 && (
                  <StepWrapper key="step-7">
                    <StepHeading
                      step="Step 7"
                      title="When do you plan to start studying?"
                      sub="Select your intended intake timeline."
                    />
                    <div className="flex flex-col gap-2.5">
                      {TIMELINES.map((t) => (
                        <SingleOption
                          key={t.id}
                          label={t.label}
                          emoji={t.emoji}
                          selected={answers.timeline === t.id}
                          highlight={
                            t.id === "early_planning" &&
                            answers.qualifications === "Form 4"
                          }
                          onClick={() => updateAnswers({ timeline: t.id })}
                        />
                      ))}
                    </div>
                    {answers.qualifications === "Form 4" && (
                      <div className="mt-4 p-3.5 rounded-2xl bg-violet-50 border border-violet-100">
                        <p
                          className="text-[12px] text-violet-700 leading-relaxed"
                          style={{ fontWeight: 500 }}
                        >
                          💡 It's great to plan early! SCS Education can help
                          you map out your path even before SPM results.
                        </p>
                      </div>
                    )}
                  </StepWrapper>
                )}

                {/* ─ Step 8: Parent Involvement ─ */}
                {step === 8 && (
                  <StepWrapper key="step-8">
                    <StepHeading
                      step="Step 8"
                      title="Are your parents involved in this decision?"
                      sub="Parents are welcome to join the discussion. SCS Education can explain fees, locations, scholarships and next steps clearly."
                    />
                    <div className="flex flex-col gap-2.5">
                      {PARENT_OPTIONS.map((p) => (
                        <SingleOption
                          key={p}
                          label={p}
                          selected={answers.parentInvolvement === p}
                          onClick={() =>
                            updateAnswers({ parentInvolvement: p })
                          }
                        />
                      ))}
                    </div>
                  </StepWrapper>
                )}

                {/* ─ Step 9: Contact / Account Creation ─ */}
                {step === 9 && (
                  <StepWrapper key="step-9">
                    <StepHeading
                      step="Step 9"
                      title="Create your student profile"
                      sub="Enter your details so SCS Education can send you personalised course suggestions and connect you with a counsellor."
                    />

                    <div className="bg-violet-50 border border-violet-100 rounded-2xl p-3.5 mb-5">
                      <p
                        className="text-[12px] text-violet-700 leading-relaxed"
                        style={{ fontWeight: 500 }}
                      >
                        🔒 Your details are kept private. SCS Education will
                        only use them to help you find the right courses and
                        universities.
                      </p>
                    </div>

                    <div className="flex flex-col gap-4">
                      {/* Full Name */}
                      <div>
                        <label
                          className="text-[12px] text-slate-700 block mb-1.5"
                          style={{ fontWeight: 600 }}
                        >
                          Full Name *
                        </label>
                        <div
                          className={`flex items-center gap-3 bg-white border-2 rounded-2xl px-4 py-3 transition-all ${errors.name ? "border-red-300" : "border-slate-200 focus-within:border-violet-400"}`}
                        >
                          <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <input
                            type="text"
                            placeholder="Your full name"
                            value={answers.name}
                            onChange={(e) =>
                              updateAnswers({
                                ...answers,
                                name: e.target.value,
                              })
                            }
                            className="flex-1 bg-transparent outline-none text-slate-800 text-[13px]"
                            style={{ fontWeight: 400 }}
                          />
                        </div>
                        {errors.name && (
                          <p
                            className="text-[11px] text-red-500 mt-1"
                            style={{ fontWeight: 500 }}
                          >
                            {errors.name}
                          </p>
                        )}
                      </div>

                      {/* Phone */}
                      <div>
                        <label
                          className="text-[12px] text-slate-700 block mb-1.5"
                          style={{ fontWeight: 600 }}
                        >
                          Phone Number *
                        </label>
                        <div
                          className={`flex items-center gap-3 bg-white border-2 rounded-2xl px-4 py-3 transition-all ${errors.phoneNumber ? "border-red-300" : "border-slate-200 focus-within:border-violet-400"}`}
                        >
                          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="relative flex items-center shrink-0">
                            <span className="text-[13px] text-slate-800 pointer-events-none">
                              +{answers.countryCode || "60"}
                            </span>
                            <select
                              value={answers.country || "Malaysia"}
                              onChange={(e) => {
                                const selectedCountry = countryData.find(
                                  (c) => c.countryName === e.target.value,
                                );
                                if (selectedCountry) {
                                  updateAnswers({
                                    ...answers,
                                    country: selectedCountry.countryName,
                                    countryCode: selectedCountry.countryCode,
                                  });
                                }
                              }}
                              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                              title="Country Code"
                            >
                              {countryData.map((country) => (
                                <option
                                  key={country.isoCode}
                                  value={country.countryName}
                                >
                                  {country.countryName} (+{country.countryCode})
                                </option>
                              ))}
                            </select>
                          </div>
                          <input
                            type="tel"
                            placeholder="123456789"
                            value={answers.phoneNumber}
                            onChange={(e) =>
                              updateAnswers({
                                ...answers,
                                phoneNumber: e.target.value,
                              })
                            }
                            className="flex-1 bg-transparent outline-none text-slate-800 text-[13px]"
                            style={{ fontWeight: 400 }}
                          />
                        </div>
                        {errors.phoneNumber && (
                          <p
                            className="text-[11px] text-red-500 mt-1"
                            style={{ fontWeight: 500 }}
                          >
                            {errors.phoneNumber}
                          </p>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <label
                          className="text-[12px] text-slate-700 block mb-1.5"
                          style={{ fontWeight: 600 }}
                        >
                          Email Address *
                        </label>
                        <div
                          className={`flex items-center gap-3 bg-white border-2 rounded-2xl px-4 py-3 transition-all ${errors.email ? "border-red-300" : "border-slate-200 focus-within:border-violet-400"}`}
                        >
                          <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <input
                            type="email"
                            placeholder="your@email.com"
                            value={answers.email}
                            onChange={(e) =>
                              updateAnswers({
                                ...answers,
                                email: e.target.value,
                              })
                            }
                            className="flex-1 bg-transparent outline-none text-slate-800 text-[13px]"
                            style={{ fontWeight: 400 }}
                          />
                        </div>
                        {errors.email && (
                          <p
                            className="text-[11px] text-red-500 mt-1"
                            style={{ fontWeight: 500 }}
                          >
                            {errors.email}
                          </p>
                        )}
                      </div>

                      {/* Password */}
                      <div>
                        <label
                          className="text-[12px] text-slate-700 block mb-1.5"
                          style={{ fontWeight: 600 }}
                        >
                          Create Password *
                        </label>
                        <div
                          className={`flex items-center gap-3 bg-white border-2 rounded-2xl px-4 py-3 transition-all ${errors.password ? "border-red-300" : "border-slate-200 focus-within:border-violet-400"}`}
                        >
                          <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <input
                            type={showPw ? "text" : "password"}
                            placeholder="At least 8 characters incl. 1 uppercase, 1 lowercase and 1 symbol"
                            value={answers.password}
                            onChange={(e) =>
                              updateAnswers({
                                ...answers,
                                password: e.target.value,
                              })
                            }
                            className="flex-1 bg-transparent outline-none text-slate-800 text-[13px]"
                            style={{ fontWeight: 400 }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPw((p) => !p)}
                            className="text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            {showPw ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {errors.password && (
                          <p
                            className="text-[11px] text-red-500 mt-1"
                            style={{ fontWeight: 500 }}
                          >
                            {errors.password}
                          </p>
                        )}
                      </div>

                      {/* Who is filling */}
                      <div>
                        <label
                          className="text-[12px] text-slate-700 block mb-1.5"
                          style={{ fontWeight: 600 }}
                        >
                          Who is filling in this form?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {FILLER_OPTIONS.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() =>
                                updateAnswers({
                                  ...answers,
                                  fillerType: opt,
                                })
                              }
                              className={`py-2.5 rounded-xl border-2 text-[11px] text-center transition-all cursor-pointer ${
                                answers.fillerType === opt
                                  ? "border-violet-500 bg-violet-50 text-violet-700"
                                  : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"
                              }`}
                              style={{
                                fontWeight:
                                  answers.fillerType === opt ? 600 : 400,
                              }}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Submit button */}
                      <button
                        onClick={handleSubmitContact}
                        disabled={isRegistering}
                        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] hover:shadow-lg hover:shadow-violet-500/30 hover:-translate-y-px transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed mt-2 cursor-pointer"
                        style={{ fontWeight: 700 }}
                      >
                        {isRegistering ? (
                          "Creating your profile..."
                        ) : (
                          <>
                            Show My Course Suggestions{" "}
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      <p
                        className="text-[11px] text-slate-400 text-center leading-relaxed"
                        style={{ fontWeight: 400 }}
                      >
                        Already have an account?{" "}
                        <NavLink
                          to="/login"
                          className="text-violet-600 hover:underline"
                          style={{ fontWeight: 600 }}
                        >
                          Sign in
                        </NavLink>
                      </p>
                    </div>
                  </StepWrapper>
                )}
              </AnimatePresence>

              {/* Navigation buttons (steps 1-8) */}
              {step >= 1 && step <= 8 && (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors text-[13px] cursor-pointer"
                    style={{ fontWeight: 600 }}
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[13px] hover:shadow-md hover:shadow-violet-500/20 hover:-translate-y-px transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none cursor-pointer"
                    style={{ fontWeight: 700 }}
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Back button for step 9 */}
              {step === 9 && (
                <div className="mt-4">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-500 hover:text-slate-700 transition-colors text-[12px] cursor-pointer"
                    style={{ fontWeight: 600 }}
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Go back to quiz
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
